/* eslint-disable unicorn/prefer-structured-clone */
/* eslint-disable @typescript-eslint/no-explicit-any */
import {
  INIT_GLOBALS,
  FETCH_POSTS_ROUTINE,
  FETCH_UPCOMING_POSTS_ROUTINE,
  FETCH_DRAFT_POSTS_ROUTINE,
  FETCH_ARCHIVED_POSTS_ROUTINE,
  REMOVE_POSTS_ROUTINE,
  REMOVE_POST_ROUTINE,
  SEND_POSTS_TO_QUEUE_ROUTINE,
  SEND_POST_TO_QUEUE_ROUTINE,
  SEND_POSTS_TO_DRAFT_ROUTINE,
  SEND_POST_TO_DRAFT_ROUTINE,
  SAVE_POST_ROUTINE,
  SORT_POST_ROUTINE,
  EDIT_POST_URL_ROUTINE,
  SHUFFLE_QUEUE_ROUTINE,
  INIT_POSTS_PAGE_ROUTINE,
  RELOAD_POSTS_ROUTINE,
  LOAD_MORE_POSTS_ROUTINE,
  TOGGLE_COLLABORATE_WIDGET,
  CHANGE_POSTS_PAGINATION,
  SAVE_POST_FORM_ROUTINE,
  UPDATE_POST_FORM_VALIDATION,
  START_POST_FORM_ROUTINE,
  SEND_POST_NOTIFICATION_ROUTINE,
  FETCH_POSTS_IGNORE,
  SKED_CORE_GET_ACCOUNTS_ROUTINE,
} from "constants/ActionTypes";
import ngDeps from "@/legacy-services";
import { chain } from "lodash";
import cloneDeep from "lodash/cloneDeep";
import concat from "lodash/concat";
import flatMap from "lodash/flatMap";
import indexOf from "lodash/indexOf";
import map from "lodash/map";

import {
  put,
  select,
  call,
  takeEvery,
  takeLatest,
  all,
  take,
  race,
  delay,
} from "redux-saga/effects";
import type { SagaIterator } from "redux-saga";
import {
  getPosts,
  getPostIds,
  getFilters,
  getLoadedPostCount,
  getUpcomingFilters,
  getPageInitialized,
} from "selectors/postsPageSelectors";
import {
  getAccountsGroups,
  getAccountsById,
  getSelectedAccountIds,
  getAccountsReady,
  getAccountsLoading,
  getAccountsFetchFailed,
} from "selectors/skedCoreSelectors";
import {
  getPage,
  getUser,
  getPagination,
  getRoutinesLoading,
} from "selectors/commonSelectors";
import { FIELDS } from "constants/PostFormFields";
import { PageName, PAGES } from "constants/Pages";
import {
  DEFAULT_UPCOMING_PAGE_SIZE,
  getInitialPostsPageSize,
} from "constants/PostsPagination";
import { POST_STATES, PostStatesType } from "constants/PostsView";
import { makePages } from "reducers/paginationReducer";
import { hideModal } from "actions/modal";
import {
  filterCollabStatus,
  filterPostContentType,
  reloadPosts as reloadPostsAction,
} from "actions/postsView";
import { partitionSelectedPostIds } from "reducers/entities/postsReducer";
import { validateFull } from "components/Posts/PostForm/postFormValidator";
import { toast } from "react-toastify";
import {
  Post,
  postsCreatedOrUpdated,
  getPostDataFromTypedPost,
} from "features/post";
import { getPostContentTypes } from "../selectors/postsPageSelectors";
import { schedulesValidate } from "api/post";
import { queryClient } from "api/query-client";
import { queryKeys } from "api/query-keys";
import { sagaErrorHandlerWrapper } from "./utils";
import {
  isNewPostId,
  standardizationPostData,
  standardizationUpcomingPosts,
} from "utils/posts";
import { getPostStatusList } from "utils/user";
import {
  postsContentAllOption,
  postsContentOptions,
} from "constants/PostsFliters";
import { fetchAccountsStatistics } from "pages/home/accountsStatistics.model";
import { selectedAccountsPostsCreatedOrUpdated } from "../entities/accounts/model/selected-accounts";
import { getLocalStorageHomeSelectedGroupName } from "libs/storage/adapters";
import {
  creatingPostNeedUploadCover,
  getUpdateThumbnail,
  mergePlatformPostFields,
  normalizePostMediaShape,
  syncCoverToMediaFiles,
} from "features/post/utils";
import {
  IOverlapSchedule,
  SaveFormResult,
} from "../components/Posts/PostForm/types";
import {
  GoogleMyBusinessPost,
  LoadedDraft,
  PlatformFields,
} from "../shared/types/post";
import {
  AccountPlatformType,
  AccountType,
  platformAccountNameMap,
} from "../shared/types/accounts";
import { PartialByKey } from "../types/utils";
import { AxiosResponse } from "axios";
import { normalizeDraft } from "../selectors/loaded-posts-normalizer";
import { getErrorMessage } from "utils/get-error-message";
import { withPostCreationSource } from "utils/post-creation-source";
import {
  getQueueDisabledAccountsForPosts,
  getQueueDisabledAccountsMessage,
} from "utils/queue";
import type { ReduxRootState } from "../types/redux-store";

type PayloadAction<T> = {
  type?: string;
  payload: T;
};

type RemovePostsPayload = {
  postIds: string[];
};

type PageActionPayload = {
  page: PageName;
};

type FetchPostsPayload = {
  currentPage: number;
  pageSize: number;
  selectedAccounts: string[];
  excludedIds?: string[];
  addMore?: boolean;
  lastNextRunAt?: number;
};

type PostActionPayload = {
  post: Post;
};

type SelectedPostsPayload = {
  selectedPostIds: string[];
  suppressSuccessAction?: boolean;
};

type SortPostPayload = {
  fromIndex: number;
  toIndex: number;
};

type PaginationPayload = {
  page: number;
  pageSize: number;
};

type NotificationPayload = {
  postId: string;
};

type ShuffleQueuePayload = {
  accountIds: string[];
};

type SagaResult<T = void> = Generator<any, T, any>;

type DraftArchiveParams = {
  order: string;
  postStatusKey?: string[];
  postContentTypes?: ReturnType<typeof getPostContentTypes>;
};

const standardizationDraftPosts = (posts: LoadedDraft[]) =>
  Promise.all(posts.map((post) => normalizeDraft(post)));
const standardizationArchivePosts = (posts: Post[]) =>
  Promise.all(posts.map((post) => standardizationPostData(post)));

const endpoints = {
  [PAGES.UPCOMING]: {
    bulkDelete: "/posts/bulk/delete",
    bulkQueue: "/posts/bulk/queue",
    bulkDraft: "/posts/bulk/drafts",
  },
  [PAGES.DRAFT]: {
    bulkDelete: "/drafts/bulk/delete",
    bulkQueue: "/drafts/bulk/queue",
  },
  [PAGES.ARCHIVE]: {
    bulkDelete: "/archives/bulk/delete",
  },
} as const;

type BulkEndpointPage = keyof typeof endpoints;
const accountPlatformTypes = Object.keys(
  platformAccountNameMap
) as AccountPlatformType[];

const isBulkEndpointPage = (page: PageName): page is BulkEndpointPage =>
  Object.prototype.hasOwnProperty.call(endpoints, page);

const isAccountPlatformType = (
  platformType: string
): platformType is AccountPlatformType =>
  accountPlatformTypes.includes(platformType as AccountPlatformType);

/**
 * Waits for accounts to be ready before proceeding.
 * If accounts are already loaded, returns immediately.
 * If accounts fetch already failed, returns false immediately.
 * If accounts are currently loading, waits for SUCCESS or FAILURE.
 * If accounts fetch hasn't started yet, waits for it to start first.
 *
 * This handles the race condition where posts page can mount before
 * useInitialUserData has fetched user data and dispatched getAccounts().
 *
 * @param timeoutMs - Maximum total time to wait (default 30 seconds)
 * @returns true if accounts are ready, false if timed out or failed
 */
export function* waitForAccountsReady(timeoutMs = 30000): SagaIterator {
  const alreadyReady: boolean = yield select(getAccountsReady);
  if (alreadyReady) {
    return true;
  }

  // Check if accounts fetch already failed - no point waiting for a new request
  // that won't come. This handles the case where user navigates away and back
  // after a failed accounts load.
  const alreadyFailed: boolean = yield select(getAccountsFetchFailed);
  if (alreadyFailed) {
    console.warn("waitForAccountsReady: accounts fetch has already failed");
    return false;
  }

  // Use a single deadline for the entire wait to avoid double-timeout
  // (waiting for REQUEST + waiting for SUCCESS could be 2x timeoutMs otherwise)
  const deadline = Date.now() + timeoutMs;

  const isLoading: boolean = yield select(getAccountsLoading);

  // If accounts fetch hasn't started yet, wait for it to begin.
  // This handles the race condition where posts page mounts before
  // useInitialUserData dispatches getAccounts().
  if (!isLoading) {
    const startResult: {
      started?: unknown;
      timeout?: boolean;
    } = yield race({
      started: take(SKED_CORE_GET_ACCOUNTS_ROUTINE.REQUEST),
      timeout: delay(timeoutMs),
    });

    if (startResult.timeout) {
      console.warn(
        "waitForAccountsReady: timed out waiting for accounts fetch to start"
      );
      return false;
    }
  }

  // Calculate remaining time from our deadline
  const remainingMs = Math.max(0, deadline - Date.now());
  if (remainingMs === 0) {
    console.warn("waitForAccountsReady: timed out waiting for accounts");
    return false;
  }

  // Now wait for the fetch to complete with remaining time budget
  const result: {
    success?: unknown;
    failure?: unknown;
    timeout?: boolean;
  } = yield race({
    success: take(SKED_CORE_GET_ACCOUNTS_ROUTINE.SUCCESS),
    failure: take(SKED_CORE_GET_ACCOUNTS_ROUTINE.FAILURE),
    timeout: delay(remainingMs),
  });

  if (result.success) {
    return true;
  }

  if (result.failure) {
    console.warn("waitForAccountsReady: accounts fetch failed");
    return false;
  }

  if (result.timeout) {
    console.warn("waitForAccountsReady: timed out waiting for accounts");
    return false;
  }

  return false;
}

type BuildUpcomingFiltersArgs = {
  addMore?: boolean;
  currentPage: number;
  excludedIds?: string[];
  lastNextRunAt?: number;
  pageSize?: number | null;
  postFilters: ReturnType<typeof getFilters>;
  postsService: typeof ngDeps.postsService;
  selectedAccounts: string[];
  selectedPostContentTypes: ReturnType<typeof getPostContentTypes>;
  upcomingFilters: ReturnType<typeof getUpcomingFilters>;
  user: ReturnType<typeof getUser>;
};

export const buildUpcomingFilters = ({
  addMore = false,
  currentPage,
  excludedIds,
  lastNextRunAt,
  pageSize,
  postFilters,
  postsService,
  selectedAccounts,
  selectedPostContentTypes,
  upcomingFilters,
  user,
}: BuildUpcomingFiltersArgs) => {
  const normalizedPageSize = pageSize ?? DEFAULT_UPCOMING_PAGE_SIZE;
  const freshFilters = postsService.makeFilters(
    selectedAccounts,
    currentPage * normalizedPageSize
  );

  const filters =
    addMore && upcomingFilters ? cloneDeep(upcomingFilters) : freshFilters;

  filters.accountFilters = selectedAccounts.reduce<
    Record<string, { lastOrdinal: number }>
  >((accountFilters, accountId) => {
    accountFilters[accountId] =
      addMore && filters.accountFilters?.[accountId]
        ? filters.accountFilters[accountId]
        : freshFilters.accountFilters[accountId];
    return accountFilters;
  }, {});

  if (!addMore) {
    filters.lastNextRunAt = freshFilters.lastNextRunAt;
    filters.limit = freshFilters.limit;
    filters.offset = freshFilters.offset;
  }

  if (postFilters.postType && postFilters.postType !== "all") {
    filters.postType = postFilters.postType;
  } else {
    delete filters.postType;
  }

  const selectedPostStatusKeys =
    postFilters.postStatusKey?.filter((key: string) => key !== "*") || [];

  if (
    user?.postStatuses &&
    selectedPostStatusKeys.length > 0 &&
    user.postStatuses.length !== selectedPostStatusKeys.length
  ) {
    filters.postStatusKey = selectedPostStatusKeys;
  } else {
    delete filters.postStatusKey;
  }

  if (selectedPostContentTypes) {
    filters.postContentTypes = selectedPostContentTypes.includes("*")
      ? []
      : selectedPostContentTypes;
  }

  if (excludedIds) {
    filters.excludedIds = excludedIds;
  } else {
    delete filters.excludedIds;
  }

  if (lastNextRunAt != null) {
    filters.lastNextRunAt = lastNextRunAt;
  }

  return filters;
};

export function* initPostsPage(
  action: PayloadAction<PageActionPayload>
): SagaIterator {
  const { page } = action.payload;
  const currentPage = 1;
  const pageSize = getInitialPostsPageSize(page);

  yield put({ type: INIT_POSTS_PAGE_ROUTINE.REQUEST, payload: action.payload });

  yield put({
    type: INIT_GLOBALS,
    payload: {
      page: page,
    },
  });

  const selectedAccountIds = yield select(getSelectedAccountIds);

  yield call(fetchPosts, {
    type: FETCH_POSTS_ROUTINE.TRIGGER,
    payload: {
      currentPage: currentPage,
      pageSize: pageSize,
      selectedAccounts: selectedAccountIds,
    },
  });

  yield put({ type: INIT_POSTS_PAGE_ROUTINE.SUCCESS });
}

export function* startPostForm(
  action: PayloadAction<Record<string, unknown>>
): SagaIterator {
  const user = yield select(getUser);
  const userTimezone = user?.timezone;
  if (!userTimezone) {
    console.error("startPostForm: user timezone is not set. User state:", user);
  } else {
    console.log("startPostForm: user timezone:", userTimezone);
  }
  yield put({
    type: START_POST_FORM_ROUTINE.SUCCESS,
    payload: { ...action.payload, userTimezone },
  });
}

function* fetchUpcoming(
  action: PayloadAction<FetchPostsPayload>
): SagaIterator {
  const { scheduleService, postsService } = ngDeps;

  const {
    currentPage,
    pageSize,
    selectedAccounts,
    excludedIds,
    addMore,
    lastNextRunAt,
  } = action.payload;
  const postFilters = yield select(getFilters);
  const selectedPostContentTypes = yield select(getPostContentTypes);
  const upcomingFilters = yield select(getUpcomingFilters);
  const user = yield select(getUser);

  const filters = buildUpcomingFilters({
    addMore,
    currentPage,
    excludedIds,
    lastNextRunAt,
    pageSize,
    postFilters,
    postsService,
    selectedAccounts,
    selectedPostContentTypes,
    upcomingFilters,
    user,
  });

  const statePosts = yield select(getPosts);

  yield put({
    type: FETCH_UPCOMING_POSTS_ROUTINE.REQUEST,
    currentPage,
    filters,
  });
  yield put({
    type: CHANGE_POSTS_PAGINATION.SUCCESS,
  });
  try {
    const data = yield call(scheduleService.findPosts, filters);

    const existing = addMore ? statePosts : [];
    const newPosts = yield call(
      standardizationUpcomingPosts,
      data.posts,
      existing
    );

    const posts = chain(existing)
      .concat(newPosts)
      .orderBy("timestamp", "asc")
      .value();

    // Create new filters
    const hasMore = data.hasMore;

    yield put({
      type: FETCH_UPCOMING_POSTS_ROUTINE.SUCCESS,
      payload: {
        posts: posts,
        filters: filters,
        hasMore: hasMore,
      },
    });
  } catch (error) {
    const message = error instanceof Error ? error.message : String(error);
    yield put({
      type: FETCH_UPCOMING_POSTS_ROUTINE.FAILURE,
      message,
      err: error,
    });
  }
}

function* fetchDrafts(action: PayloadAction<FetchPostsPayload>): SagaIterator {
  const { scheduleService } = ngDeps;
  const { currentPage, pageSize, selectedAccounts } = action.payload;
  const postFilters = yield select(getFilters);
  const selectedPostContentTypes = yield select(getPostContentTypes);
  const page: PageName = yield select(getPage);
  const params: DraftArchiveParams = { order: "desc" };
  if (
    postFilters.postStatusKey &&
    (page === PAGES.DRAFT || page === PAGES.UPCOMING || page === PAGES.CALENDAR)
  ) {
    params.postStatusKey = postFilters.postStatusKey;
  }

  if (selectedPostContentTypes) {
    params.postContentTypes = selectedPostContentTypes.includes("*")
      ? []
      : selectedPostContentTypes;
  }

  yield put({ type: FETCH_DRAFT_POSTS_ROUTINE.REQUEST, currentPage });
  try {
    const data = yield call(
      scheduleService.findDrafts,
      currentPage,
      pageSize,
      selectedAccounts,
      params
    );
    const posts = yield call(standardizationDraftPosts, data.schedules);
    const total = data.total;
    const lastPage = Math.ceil(data.total / pageSize) || 1;

    yield put({
      type: FETCH_DRAFT_POSTS_ROUTINE.SUCCESS,
      payload: {
        posts,
        total: total,
        pagination: {
          currentPage: currentPage,
          lastPage: lastPage,
          pageSize: pageSize,
          pages: makePages(data.schedules, total, currentPage, lastPage),
        },
      },
    });
  } catch (error) {
    const message = error instanceof Error ? error.message : String(error);
    yield put({
      type: FETCH_DRAFT_POSTS_ROUTINE.FAILURE,
      message,
      err: error,
    });
  }
}

export function* fetchArchived(
  action: PayloadAction<FetchPostsPayload>
): SagaIterator {
  const { scheduleService } = ngDeps;
  const { currentPage, pageSize, selectedAccounts } = action.payload;
  const postFilters = yield select(getFilters);
  const selectedPostContentTypes = yield select(getPostContentTypes);

  const params: DraftArchiveParams = { order: "desc" };

  if (postFilters.postStatusKey?.length) {
    params.postStatusKey = postFilters.postStatusKey;
  }

  if (selectedPostContentTypes) {
    params.postContentTypes = selectedPostContentTypes.includes("*")
      ? []
      : selectedPostContentTypes;
  }

  yield put({
    type: FETCH_ARCHIVED_POSTS_ROUTINE.REQUEST,
    payload: { currentPage, pageSize, selectedAccounts },
  });

  try {
    const data = yield call(
      scheduleService.findArchived,
      currentPage,
      pageSize,
      selectedAccounts,
      params
    );

    const total = data.total;
    const lastPage = Math.ceil(data.total / pageSize) || 1;
    const posts = yield call(standardizationArchivePosts, data.schedules);

    yield put({
      type: FETCH_ARCHIVED_POSTS_ROUTINE.SUCCESS,
      payload: {
        posts,
        total: total,
        pagination: {
          currentPage: currentPage,
          lastPage: lastPage,
          pageSize: pageSize,
          pages: makePages(data.schedules, total, currentPage, lastPage),
        },
      },
    });
  } catch (error) {
    const message = error instanceof Error ? error.message : String(error);
    yield put({
      type: FETCH_ARCHIVED_POSTS_ROUTINE.FAILURE,
      message,
      err: error,
    });
  }
}

export function* fetchPosts(
  action: PayloadAction<FetchPostsPayload>
): SagaIterator {
  // Wait for accounts to be loaded before attempting to fetch posts
  // This prevents race conditions where posts are fetched before accounts are available,
  // which causes "No accounts for post" errors in components
  const accountsReady: boolean = yield call(waitForAccountsReady);
  if (!accountsReady) {
    yield put({
      type: FETCH_POSTS_IGNORE,
      payload: {
        message: "Accounts not ready",
        error: "Unable to load accounts. Please refresh the page to try again.",
      },
    });
    return;
  }

  const page: PageName = yield select(getPage);
  const fetchTypes = {
    [PAGES.PLANNER]: () => false,
    [PAGES.UPCOMING]: fetchUpcoming,
    [PAGES.DRAFT]: fetchDrafts,
    [PAGES.CALENDAR]: fetchDrafts,
    [PAGES.ARCHIVE]: fetchArchived,
  };

  if (
    !action.payload.selectedAccounts ||
    action.payload.selectedAccounts.length === 0
  ) {
    yield put({
      type: FETCH_POSTS_IGNORE,
      payload: { message: "No accounts selected" },
    });
    return;
  }

  const fetcher = fetchTypes[page as keyof typeof fetchTypes];
  yield call(fetcher, action);
}

export function* reloadPosts(): SagaIterator {
  const { currentPage, pageSize } = yield select(getPagination);
  const selectedAccountIds = yield select(getSelectedAccountIds);
  const posts = yield select(getPosts);
  const page = yield select(getPage);
  const pageInitialized = yield select(getPageInitialized);
  const isFetching = yield select((state: ReduxRootState) =>
    getRoutinesLoading(state, [
      FETCH_UPCOMING_POSTS_ROUTINE,
      FETCH_DRAFT_POSTS_ROUTINE,
      FETCH_ARCHIVED_POSTS_ROUTINE,
    ])
  );

  // fix wrong Calendar page init, we need to reload drafts posts after
  // the new post was created

  if (isFetching || (!pageInitialized && page !== PAGES.CALENDAR)) {
    return;
  }

  if (!selectedAccountIds || selectedAccountIds.length === 0) {
    return;
  }

  let pageToFetch = currentPage;
  if (currentPage !== 1 && posts.length === 1) {
    pageToFetch -= 1;
  }

  if (page === PAGES.UPCOMING) {
    // todo reload for activities
    // return dispatch(fetchPosts())
  }

  yield call(fetchPosts, {
    type: FETCH_POSTS_ROUTINE.TRIGGER,
    payload: {
      currentPage: pageToFetch,
      pageSize,
      selectedAccounts: selectedAccountIds,
    },
  });
}

// Actions
export function* removePosts(
  action: PayloadAction<RemovePostsPayload>
): SagaIterator {
  const { $http } = ngDeps;
  const { postIds } = action.payload;

  if (postIds.length === 0) {
    yield put(hideModal());
    return;
  }

  const page = yield select(getPage);
  const posts = yield select(getPosts);

  const idGroups = partitionSelectedPostIds(posts, postIds);
  yield put({ type: REMOVE_POSTS_ROUTINE.REQUEST });

  try {
    if (!isBulkEndpointPage(page)) {
      throw new Error(`No bulk delete endpoint for page ${page}`);
    }
    yield call($http.post, endpoints[page].bulkDelete, idGroups);
    yield put({
      type: REMOVE_POSTS_ROUTINE.SUCCESS,
      payload: {
        removedPostIds: postIds,
      },
    });
    yield put(reloadPostsAction());
  } catch (error) {
    yield put({
      type: REMOVE_POSTS_ROUTINE.FAILURE,
      payload: error,
      error: true,
    });
  } finally {
    yield put(hideModal());
  }
}

export function* removePost(
  action: PayloadAction<PostActionPayload>
): SagaIterator {
  const { $http, scheduleService } = ngDeps;
  const page = yield select(getPage);
  const { post } = action.payload;

  let removePostCall: ((...args: any[]) => Promise<any>) | null = null;
  let removePostParams: any[] = [];
  switch (page) {
    case PAGES.UPCOMING: {
      if (post.queued) {
        removePostCall = $http.delete;
        removePostParams.push("/queue/" + post._id);
        break;
      }

      removePostCall = scheduleService.cancel;
      removePostParams.push(post._id);
      break;
    }
    case PAGES.DRAFT: {
      removePostCall = $http.delete;
      removePostParams.push("/drafts/" + post._id, {
        success: "Removed draft successfully",
        error: "An error occurred in removing draft",
      });
      break;
    }
    case PAGES.ARCHIVE: {
      if (post.isQueued) {
        removePostCall = scheduleService.removeFromQueue;
        removePostParams.push(post._id);
        break;
      }
      removePostCall = scheduleService.remove;
      removePostParams.push(post._id);
      break;
    }
  }

  yield put({
    type: REMOVE_POSTS_ROUTINE.REQUEST,
    payload: { removedPostIds: [post._id] },
  });

  try {
    if (!removePostCall) {
      throw new Error(`No removePost handler for page ${page}`);
    }

    yield call(removePostCall, ...removePostParams);

    yield put({
      type: REMOVE_POSTS_ROUTINE.SUCCESS,
      payload: {
        removedPostIds: [post._id],
      },
    });

    yield put(reloadPostsAction());
  } catch (error) {
    yield put({
      type: REMOVE_POSTS_ROUTINE.FAILURE,
      payload: error,
      error: true,
    });
  } finally {
    yield put(hideModal());
  }
}

export function* sendPostsToQueue(
  action: PayloadAction<SelectedPostsPayload>
): SagaIterator {
  const { $http } = ngDeps;
  const { selectedPostIds, suppressSuccessAction = false } = action.payload;

  const page: PageName = yield select(getPage);
  const posts = yield select(getPosts);
  const accountsById = yield select(getAccountsById);

  const idGroups = partitionSelectedPostIds(posts, selectedPostIds);
  const selectedPosts = posts.filter((post: Post) =>
    idGroups.postIds.includes(post._id)
  );
  const queueDisabledAccounts = getQueueDisabledAccountsForPosts(
    selectedPosts,
    accountsById
  );

  let endpointPayload = { postIds: idGroups.postIds };
  if (idGroups.postIds.length === 0) {
    toast.warning("You can only queue posts that are not already queued.");
    yield put(hideModal());
    return;
  }
  if (queueDisabledAccounts.length > 0) {
    toast.error(getQueueDisabledAccountsMessage(queueDisabledAccounts));
    yield put(hideModal());
    return;
  }

  yield put({ type: SEND_POSTS_TO_QUEUE_ROUTINE.REQUEST });
  try {
    if (page !== PAGES.UPCOMING && page !== PAGES.DRAFT) {
      throw new Error(`No bulk queue endpoint for page ${page}`);
    }
    yield call(
      { context: $http, fn: $http.post },
      endpoints[page].bulkQueue,
      endpointPayload
    );
    toast.success(
      `Successfully sent your post${
        idGroups.postIds.length > 1 ? "s" : ""
      } to queue.`
    );
    if (!suppressSuccessAction) {
      yield put({
        type: SEND_POSTS_TO_QUEUE_ROUTINE.SUCCESS,
        payload: {
          selectedPostIds: idGroups.postIds,
        },
      });
    }
    yield put(reloadPostsAction());
  } catch (error) {
    toast.error(getErrorMessage(error));
    yield put({
      type: SEND_POSTS_TO_QUEUE_ROUTINE.FAILURE,
      payload: error,
      error: true,
    });
    throw error;
  } finally {
    yield put(hideModal());
  }
}

export function* sendPostToQueue(
  action: PayloadAction<PostActionPayload>
): SagaIterator {
  const { post } = action.payload;
  const accountsById = yield select(getAccountsById);
  const page = yield select(getPage);
  const queueDisabledAccounts = getQueueDisabledAccountsForPosts(
    [post],
    accountsById
  );

  if (queueDisabledAccounts.length > 0) {
    toast.error(getQueueDisabledAccountsMessage(queueDisabledAccounts));
    yield put(hideModal());
    return;
  }

  if (page === PAGES.DRAFT) {
    try {
      yield call(sendPostsToQueue, {
        type: SEND_POSTS_TO_QUEUE_ROUTINE.TRIGGER,
        payload: {
          selectedPostIds: [post._id],
          suppressSuccessAction: true,
        },
      });
      yield put({
        type: SEND_POST_TO_QUEUE_ROUTINE.SUCCESS,
        payload: {
          page: PAGES.UPCOMING,
          selectedPostIds: [post._id],
        },
      });
    } catch {
      // Bulk queue flow already dispatches/toasts the failure for draft posts.
    }
    return;
  }

  let queuedPost = Object.assign({}, post);
  queuedPost.accountIds = queuedPost.accountId ? [queuedPost.accountId] : [];
  queuedPost.queued = true;
  try {
    yield call(savePost, {
      type: SAVE_POST_ROUTINE.TRIGGER,
      payload: {
        post: queuedPost,
      },
    });

    yield put({
      type: SEND_POST_TO_QUEUE_ROUTINE.SUCCESS,
      payload: {
        page: PAGES.UPCOMING,
        selectedPostIds: [post._id],
      },
    });

    yield put(reloadPostsAction());
  } catch (error) {
    toast.error(getErrorMessage(error));
    yield put({
      type: SEND_POST_TO_QUEUE_ROUTINE.FAILURE,
      error: true,
      payload: error,
    });
  } finally {
    yield put(hideModal());
  }
}

export function* sendPostsToDraft(action: {
  payload: { selectedPostIds: string[] };
}): SagaIterator {
  const { selectedPostIds } = action.payload;
  const { $http } = ngDeps;

  const page: ReturnType<typeof getPage> = yield select(getPage);
  const posts: ReturnType<typeof getPosts> = yield select(getPosts);

  const idGroups = partitionSelectedPostIds(posts, selectedPostIds);
  yield put({ type: SEND_POSTS_TO_DRAFT_ROUTINE.REQUEST });

  try {
    yield call($http.post, endpoints[page as "UPCOMING"].bulkDraft, idGroups);
    toast.success(
      `Successfully sent your post${
        idGroups.postIds.length > 1 ? "s" : ""
      } to draft.`
    );
    yield put({
      type: SEND_POSTS_TO_DRAFT_ROUTINE.SUCCESS,
      payload: {
        selectedPostIds,
      },
    });
    yield put(reloadPostsAction());
  } catch (error) {
    yield put({
      type: SEND_POSTS_TO_DRAFT_ROUTINE.TRIGGER,
      error: true,
      payload: error,
    });
    throw error;
  } finally {
    yield put(hideModal());
  }
}

export function* sendPostToDraft(
  action: PayloadAction<PostActionPayload>
): SagaIterator {
  const { scheduleService } = ngDeps;
  const { post } = action.payload;

  yield put({ type: SEND_POST_TO_DRAFT_ROUTINE.REQUEST });

  try {
    //archived passed false for the 2nd param but should be okay passing like this?
    yield call(scheduleService.moveToDraft, post._id, post.queued ?? false);
    yield put({
      type: SEND_POST_TO_DRAFT_ROUTINE.SUCCESS,
    });

    yield put(reloadPostsAction());
  } catch (error) {
    yield put({
      type: SEND_POST_TO_DRAFT_ROUTINE.FAILURE,
      error: true,
      payload: error,
    });
  } finally {
    yield put(hideModal());
  }
}

export function* savePost(
  action: PayloadAction<PostActionPayload>
): SagaResult {
  const { scheduleService } = ngDeps;
  const { post } = action.payload;

  yield put({
    type: SAVE_POST_ROUTINE.REQUEST,
  });

  try {
    const normalizedPost = normalizePostMediaShape(post);
    const promise = scheduleService.savePost(normalizedPost);
    yield all([promise]);
    yield put({
      type: SAVE_POST_ROUTINE.SUCCESS,
      payload: {
        post: normalizedPost,
      },
    });
  } catch (error) {
    yield put({
      type: SAVE_POST_ROUTINE.FAILURE,
      error: true,
      payload: error,
    });
    throw error;
  } finally {
    yield put(hideModal());
  }
}

export function* sortPost(
  action: PayloadAction<SortPostPayload>
): SagaIterator {
  const { $http } = ngDeps;
  const { fromIndex, toIndex } = action.payload;

  const posts = yield select(getPosts);
  const page = yield select(getPage);

  if (page !== PAGES.UPCOMING) {
    return;
  }

  let fromPost = cloneDeep(posts[fromIndex]);
  let toPost = cloneDeep(posts[toIndex]);

  if (!fromPost.queued || !toPost.queued) {
    toast.error(
      "Posts cannot be drag when there is a mixture of scheduled and queued posts. This feature is only available when you use the queue feature exclusively."
    );

    return;
  }

  if (fromPost.accountId !== toPost.accountId) {
    toast.error(
      "Drag the post above or below a queued post for the same account."
    );

    return;
  }

  let when = fromPost.when;
  fromPost.when = toPost.when;
  toPost.when = when;

  yield put({
    type: SORT_POST_ROUTINE.REQUEST,
    payload: { fromIndex, toIndex },
  });

  try {
    yield call(
      $http.post,
      `/queue/${fromPost.accountId}/reorder`,
      { fromPostId: fromPost._id, toPostId: toPost._id },
      { success: "Post moved successfully" }
    );

    yield put({
      type: SORT_POST_ROUTINE.SUCCESS,
      payload: {
        fromIndex,
        toIndex,
      },
    });

    yield put(reloadPostsAction());
  } catch (error) {
    yield put({
      type: SORT_POST_ROUTINE.FAILURE,
      error: true,
      payload: error,
    });
  }
}

export function* loadMorePosts(
  _action: PayloadAction<Record<string, never>>
): SagaIterator {
  const selectedAccountIds = yield select(getSelectedAccountIds);

  const loadedPostCount = yield select(getLoadedPostCount);
  const posts = yield select(getPosts);

  const excludedIds = posts.map((post: Post) => post._id);

  const pageSize = 10;
  const nextPageToLoad = Math.ceil(loadedPostCount / pageSize) + 1;
  const lastNextRunAt = posts.at(-1)?.timestamp;
  // currentPage = currentPage > 0 ? currentPage - 1 : 0;
  console.log(loadedPostCount, pageSize, nextPageToLoad);

  yield put({
    type: CHANGE_POSTS_PAGINATION.TRIGGER,
    payload: { page: nextPageToLoad, pageSize },
  });

  yield call(fetchUpcoming, {
    type: FETCH_UPCOMING_POSTS_ROUTINE.TRIGGER,
    payload: {
      currentPage: nextPageToLoad,
      pageSize,
      selectedAccounts: selectedAccountIds,
      excludedIds,
      addMore: true,
      lastNextRunAt,
    },
  });
}

export function* editPostUrl(
  action: PayloadAction<PostActionPayload>
): SagaIterator {
  const { postsController, scheduleService } = ngDeps;
  const { post } = action.payload;

  yield put({ type: EDIT_POST_URL_ROUTINE.REQUEST });

  try {
    yield call(scheduleService.updateRedirectUrl, post);

    if (postsController) {
      delete postsController.currentPostUrl;
      delete postsController.post;
    }

    yield put({
      type: EDIT_POST_URL_ROUTINE.SUCCESS,
      payload: {
        post,
      },
    });
  } catch (error) {
    yield put({
      type: EDIT_POST_URL_ROUTINE.FAILURE,
      error: true,
      payload: error,
    });
  } finally {
    postsController?.closeModal();
  }
}

export function* shuffleQueue(
  action: PayloadAction<ShuffleQueuePayload>
): SagaIterator {
  const { $http, $rootScope } = ngDeps;
  const { accountIds } = action.payload;

  yield put({ type: SHUFFLE_QUEUE_ROUTINE.REQUEST });
  try {
    yield call($http.post, "/queue/shuffle", { accountIds: accountIds });
    toast.success("Queue shuffled successfully");
    yield put({ type: SHUFFLE_QUEUE_ROUTINE.SUCCESS });

    yield put(reloadPostsAction());
  } catch (error) {
    yield put({
      type: SHUFFLE_QUEUE_ROUTINE.FAILURE,
      error: true,
      payload: error,
    });
  } finally {
    $rootScope.closeModal();
  }
}
export function* sendPostNotification(
  action: PayloadAction<NotificationPayload>
): SagaIterator {
  const { $http } = ngDeps;
  const { postId } = action.payload;

  yield put({ type: SEND_POST_NOTIFICATION_ROUTINE.REQUEST });
  try {
    yield call($http.post, `/schedules/${postId}/sendNotification`, { postId });
    toast.success(
      "Sent notification to you via email and push (if available)."
    );
    yield put({ type: SEND_POST_NOTIFICATION_ROUTINE.SUCCESS });
  } catch (error) {
    yield put({
      type: SEND_POST_NOTIFICATION_ROUTINE.FAILURE,
      error: true,
      payload: error,
    });
  } finally {
  }
}

export function* toggleCollaborateWidget(
  action: PayloadAction<PostActionPayload>
): SagaIterator {
  const { post } = action.payload;
  const postIds = yield select(getPostIds);
  const index = indexOf(postIds, post._id);
  const { postsController } = ngDeps;
  if (index === -1) {
    return;
  }

  postsController.toggleCollaborateWidget(index, cloneDeep(post));
}

export function* changePostsPagination(
  action: PayloadAction<PaginationPayload>
): SagaIterator {
  const { page, pageSize } = action.payload;
  // const selectedCollection = yield select(getSelectedCollection);
  // const { tags, name } = yield select(getMediaLibraryFilters);
  yield put({
    type: CHANGE_POSTS_PAGINATION.REQUEST,
    payload: { page, pageSize },
  });

  try {
    yield call(reloadPosts);
    yield put({
      type: CHANGE_POSTS_PAGINATION.SUCCESS,
    });
  } catch (error) {
    yield put({
      type: CHANGE_POSTS_PAGINATION.FAILURE,
      error: true,
      payload: error,
    });
  }
}

export function* savePostForm(action: {
  payload: {
    form: SaveFormResult;
    isControlled?: boolean;
  };
}): SagaResult<string[] | void> {
  const { form, isControlled } = action.payload;
  const { scheduleService, postService } = ngDeps;
  yield put({
    type: SAVE_POST_FORM_ROUTINE.REQUEST,
    payload: { form },
  });

  try {
    let { post, originalPostState } = form;
    const { platformFields, newPostState, platforms } = form;
    const isLogicalCreation = Boolean(post.newPost);
    const isSingleQueueConversion =
      !isControlled &&
      !isLogicalCreation &&
      newPostState === POST_STATES.QUEUED &&
      originalPostState !== POST_STATES.QUEUED;

    const ensurePostPlatforms = <T extends Post>(postToSave: T): T => {
      if (postToSave.platforms?.length) {
        return postToSave;
      }

      return {
        ...postToSave,
        platforms: platforms.map((platform) => platform.TYPE),
      };
    };

    const selectedPlatformField =
      platforms.length === 1 ? platformFields[platforms[0].TYPE] : undefined;

    if (selectedPlatformField) {
      post = mergePlatformPostFields(post, selectedPlatformField);
    }

    const page: ReturnType<typeof getPage> = yield select(getPage);
    const user: ReturnType<typeof getUser> = yield select(getUser);
    const overlapSchedule: IOverlapSchedule = yield select(
      (state: {
        forms: {
          postForms: {
            byId: Record<string, { overlapSchedule: IOverlapSchedule }>;
          };
        };
      }) => state.forms.postForms.byId[post._id]?.overlapSchedule
    );

    const [errors, warnings] = validateFull(
      newPostState,
      post,
      platformFields,
      platforms,
      overlapSchedule,
      user?.timezone
    );

    // Need to fix errors before we can save;
    if (errors && Object.keys(errors).length > 0) {
      yield put({
        type: UPDATE_POST_FORM_VALIDATION,
        payload: { postId: post["_id"], errors, warnings },
      });
      yield put({
        type: SAVE_POST_FORM_ROUTINE.FAILURE,
        error: false,
        payload: "",
      });
      return;
    }

    // for archived posts when we "post again" they should
    // be treated same as scheduled
    const postServiceMap = {
      [POST_STATES.DRAFT]: "draft",
      [POST_STATES.QUEUED]: "queue",
      [POST_STATES.SCHEDULED]: "schedule",
    } as const;

    // archived posts are scheduled posts
    if (originalPostState === "ARCHIVED") {
      originalPostState = "SCHEDULED";
      if (!post.when) {
        // this is because we have to save the current post before
        // then moving to drafts etc etc
        // only change this if the user has not otherwise selected a time
        // otherwise it will throw an error because it will try and save with a schedule for the current datetime
        post = cloneDeep(post);
        post.when = new Date(Date.now() + 5 * 60 * 1000);
      }
    }
    const originalEntity = postServiceMap[originalPostState];
    type PostServiceVerb =
      (typeof postServiceMap)[keyof typeof postServiceMap] | "update";
    let postServiceVerb: PostServiceVerb = "update"; // Assume update
    // We have a state change
    if (originalPostState !== newPostState) {
      postServiceVerb =
        postServiceMap[newPostState as Exclude<PostStatesType, "ARCHIVED">];
    }

    // If we are still in draft then we may need to split up the post manually.
    // Due to the unlinking logic.
    const postsObj = buildPosts(post, platformFields, page, newPostState);

    // Only save the original post if there are still accounts.
    // If there aren't any accounts, it means all the platforms were unlinked
    if (postsObj.originalPost && postsObj.originalPost.accounts.length > 0) {
      // Handle cover upload for original post if needed, and strip videoCoverPreview
      // (videoCoverPreview is a client-side preview URL that should never be persisted)
      let originalPostToSave = ensurePostPlatforms(postsObj.originalPost);
      if (creatingPostNeedUploadCover(originalPostToSave)) {
        originalPostToSave = {
          ...originalPostToSave,
          thumbnailUrl: yield getUpdateThumbnail(originalPostToSave),
        } as Post;
      }
      // Strip client-only cover preview state before saving.
      const {
        videoCoverPreview: _vcp,
        videoCoverPreviewSource: _vcps,
        videoCoverOriginal: _vco,
        videoCoverOriginalTimestamp: _vcot,
        videoCoverOriginalType: _vcotype,
        ...originalPostWithoutPreview
      } = originalPostToSave;
      originalPostToSave = normalizePostMediaShape(
        syncCoverToMediaFiles(
          ensurePostPlatforms(originalPostWithoutPreview as Post)
        )
      );

      // From  => To     : Needs Save Before Convert :
      // Schedule => Draft  : Y :
      // Schedule => Queue  : Y :
      // Queue => Draft     : Y :
      // Draft => Queue     : Y :
      // Queue => Schedule  : N :
      // Draft => Schedule  : N :
      if (
        postServiceVerb !== "update" &&
        newPostState !== POST_STATES.SCHEDULED
      ) {
        const originalPostUpdatePromise =
          postService[originalEntity]["update"](originalPostToSave);
        yield all([originalPostUpdatePromise]);
      }
      const postServiceMethod = postService[originalEntity][postServiceVerb];
      if (!postServiceMethod) {
        throw new Error(`Missing postService method ${postServiceVerb}`);
      }
      const originalPostPromise = postServiceMethod(originalPostToSave);

      yield all([originalPostPromise]);
    }

    const extractNewPosts = (newPosts: Post[]) => {
      const promises = newPosts.map(async (newPost) => {
        newPost = ensurePostPlatforms(newPost);
        if (creatingPostNeedUploadCover(newPost)) {
          newPost = {
            ...newPost,
            thumbnailUrl: await getUpdateThumbnail(newPost),
          } as Post;
        }
        // Strip client-only cover preview state before saving.
        const {
          videoCoverPreview: _vcp,
          videoCoverPreviewSource: _vcps,
          videoCoverOriginal: _vco,
          videoCoverOriginalTimestamp: _vcot,
          videoCoverOriginalType: _vcotype,
          ...newPostWithoutPreview
        } = newPost;
        const postToSave = normalizePostMediaShape(
          syncCoverToMediaFiles(
            ensurePostPlatforms(newPostWithoutPreview as Post)
          )
        );

        // create payload — use getPostDataFromTypedPost to ensure proper
        // field normalisation (postType, root-level media data, platform fields)
        const actionType =
          postServiceMap[newPostState as Exclude<PostStatesType, "ARCHIVED">];
        const newFormattedPost = {
          actionType,
          ...getPostDataFromTypedPost(postToSave),
        };

        return {
          accountIds: postToSave["accountIds"],
          schedules: [newFormattedPost],
        };
      });

      return Promise.all(promises);
    };

    const newPosts: Awaited<ReturnType<typeof extractNewPosts>> =
      yield extractNewPosts(postsObj.newPosts);

    const serverValidationPromises = newPosts.map(
      ({ accountIds, schedules }) => {
        return schedulesValidate({
          accountIds,
          schedules,
          timezone: user.timezone,
          uploader: "multiposter",
        });
      }
    );

    if (serverValidationPromises.length > 0) {
      yield all(serverValidationPromises);
    }

    const promises = newPosts.map(({ accountIds, schedules }) => {
      return scheduleService.create(
        withPostCreationSource(
          {
            accountIds,
            schedules,
            timezone: user.timezone,
          },
          {
            isBulkCreate: Boolean(isControlled),
            isLogicalCreation,
            isSingleConversion: isSingleQueueConversion,
          }
        ),
        "multiposter",
        true
      );
    });

    let newPostCreatedData: AxiosResponse<string[]>[] = [];
    if (promises.length > 0) {
      newPostCreatedData = yield all(promises);
    }

    yield put({
      type: SAVE_POST_FORM_ROUTINE.SUCCESS,
      payload: {
        postId: post._id,
        originalPost: postsObj.originalPost,
        newPosts: postsObj.newPosts,
      },
    });

    void queryClient.invalidateQueries({
      queryKey: queryKeys.calendar.postLists,
      refetchType: "active",
    });
    void queryClient.invalidateQueries({
      queryKey: queryKeys.calendar.draftLists,
      refetchType: "active",
    });

    // Only Edit
    const newPostLength = postsObj.newPosts ? postsObj.newPosts.length : 0;

    const toastSuccess = (text: string) => {
      if (!isControlled) {
        toast.success(text);
      }
    };

    if (postsObj.originalPost && newPostLength === 0) {
      toastSuccess(`Successfully edited post`);
      // Edit + Create
    } else if (postsObj.originalPost && newPostLength > 0) {
      toastSuccess(
        `Successfully edited original post and created ${
          newPostLength === 1 ? "a post" : `${newPostLength} posts`
        }`
      );
      // Only Create
    } else if (!postsObj.originalPost && newPostLength > 0) {
      toastSuccess("Post created! Your page filters have been reset.");

      // NOTE only if we add post from sked Homepage, and if we add new post for account that selected on sked home, need update sked home statistic info
      const currentRoutePathname = window.location.pathname;
      const selectedGroupName = getLocalStorageHomeSelectedGroupName();

      if (
        ["/dashboard", "/dashboard/"].includes(currentRoutePathname) &&
        selectedGroupName &&
        !isControlled
      ) {
        const accountsGroups: ReturnType<typeof getAccountsGroups> =
          yield select(getAccountsGroups);
        const selectedGroup = accountsGroups.find(
          (group) => group.name === selectedGroupName
        );

        // eslint-disable-next-line unicorn/prefer-ternary
        if (selectedGroup) {
          yield put(
            fetchAccountsStatistics({
              groupName: selectedGroup.name,
              accountsIds: selectedGroup.accountIds,
            })
          );
        } else {
          // no selectedGroup due to first created account with default group
          yield put(
            fetchAccountsStatistics({
              groupName: selectedGroupName,
              accountsIds: newPosts[0]?.accountIds,
            })
          );
        }
      }
    }
    if (isControlled) {
      return flatMap(newPostCreatedData, (r) => r.data);
    }
    yield put(postsCreatedOrUpdated({ post: postsObj }));
    yield put(selectedAccountsPostsCreatedOrUpdated(postsObj.newPosts));

    if (isNewPostId(post._id)) {
      // If new post is created, change filters to default (select all) -> that this auto run reloadPostsAction
      const userCollabSettings = getPostStatusList(user);
      const initialPostStatuses = userCollabSettings.map((item) => item.key);

      yield put(filterCollabStatus(initialPostStatuses));
      yield put(
        filterPostContentType(
          [postsContentAllOption, ...postsContentOptions].map(
            (item) => item.value
          )
        )
      );
    } else {
      yield put(reloadPostsAction());
    }
  } catch (error) {
    console.error(error);
    if (isControlled) {
      throw error;
    }
    yield put({
      type: SAVE_POST_FORM_ROUTINE.FAILURE,
      error: true,
      payload: {
        message: getErrorMessage(error),
        postId: form.post._id,
      },
    });
  }
}

export const buildPosts = (
  _post: Post,
  platformFields: PlatformFields,
  page: PageName,
  postState: PostStatesType
): {
  originalPost: Post;
  newPosts: Post[];
} => {
  const newPosts = [];
  const post = cleanPost(_post);

  // No need to split up
  if (!platformFields && page !== PAGES.UPCOMING) {
    return { originalPost: post, newPosts: [] };
  }

  type TempPostState = PartialByKey<
    Post,
    "accountId" | "accountIds" | "accounts" | "accountName" | "_id"
  >;
  // We will fill in the accounts later
  let originalPost: TempPostState | null = cloneDeep(post);
  delete originalPost["accountId"];
  delete originalPost["accountIds"];
  delete originalPost["accounts"];
  delete originalPost["accountName"];

  // take off accounts
  const accounts = post.accounts;

  // Helper function to apply platform-specific video type logic
  const applyPlatformSpecificVideoType = (
    postData: TempPostState,
    platformType: AccountPlatformType
  ) => {
    if (postData.videoPostType === "reel" && postData.isVideo) {
      // Only Facebook supports fb-reel type, all other platforms should use video
      if (platformType === "FB") {
        postData.type = "fb-reel";
        postData.isFbReel = true;
      } else {
        // For Instagram and other platforms, keep as video even if user selected reel
        postData.type = "video";
        // Remove fb-reel specific fields for non-FB platforms
        delete postData.isFbReel;
      }
    }
    return postData;
  };

  // upcoming and archive are the same
  const isUpcoming = page === PAGES.UPCOMING || page === PAGES.ARCHIVE;
  // if it's a draft then it should be created as cluster
  const isCreatingDraft = postState === POST_STATES.DRAFT;
  if (isUpcoming && !isCreatingDraft) {
    for (const [i, upcomingAccount] of accounts.entries()) {
      const upcomingAccountType = upcomingAccount.platformType || "IG";
      if (i === 0) {
        originalPost["accounts"] = [upcomingAccount];
        originalPost["accountIds"] = [upcomingAccount["_id"]];
        // Apply platform-specific video type logic to original post
        originalPost = applyPlatformSpecificVideoType(
          originalPost,
          upcomingAccountType
        );
      }

      let newUpcomingPost = cloneDeep(originalPost);
      newUpcomingPost["accounts"] = [upcomingAccount];
      newUpcomingPost["accountIds"] = [upcomingAccount["_id"]];

      // Apply platform-specific video type logic
      newUpcomingPost = applyPlatformSpecificVideoType(
        newUpcomingPost,
        upcomingAccountType
      );

      if (
        platformFields[upcomingAccountType] &&
        platformFields[upcomingAccountType]["unlinked"]
      ) {
        newUpcomingPost = mergePlatformPostFields(
          newUpcomingPost as Post,
          platformFields[upcomingAccountType] as Partial<Post>
        ) as typeof newUpcomingPost;
        delete newUpcomingPost["unlinked"];
        // Re-apply platform-specific video type logic after merging platform fields
        newUpcomingPost = applyPlatformSpecificVideoType(
          newUpcomingPost,
          upcomingAccountType
        );
      }

      delete newUpcomingPost["_id"];
      delete newUpcomingPost["newPost"];
      newPosts.push(newUpcomingPost);
    }

    originalPost = null;
  } else {
    // Draft => (Queue / Scheduled)
    const platformAccounts: Partial<
      Record<AccountPlatformType, AccountType[]>
    > = {};
    accounts.map((account) => {
      const platformType = account.platformType || "IG";
      if (!platformAccounts[platformType]) {
        platformAccounts[platformType] = [];
      }
      platformAccounts[platformType].push(account);
    });

    (Object.keys(platformFields) as Array<keyof typeof platformFields>).forEach(
      (platformType) => {
        if (!isAccountPlatformType(platformType)) {
          return;
        }
        let platformPost = mergePlatformPostFields(
          originalPost as Post,
          platformFields[platformType] as Partial<Post>
        ) as TempPostState;

        // If a user unlinked a platform then removed the accounts then
        // the platform fields would still be set but there isn't an
        // account, so ignore.
        if (!platformAccounts[platformType]) {
          return;
        }

        const accounts = platformAccounts[platformType];
        const accountIds = map(accounts, "_id");

        platformPost["accounts"] = accounts;
        platformPost["accountIds"] = accountIds;

        // Apply platform-specific video type logic
        platformPost = applyPlatformSpecificVideoType(
          platformPost,
          platformType
        );

        if (platformPost["unlinked"]) {
          delete platformAccounts[platformType];
          delete platformPost["unlinked"];
          delete platformPost["_id"];
          delete platformPost["newPost"];
          newPosts.push(platformPost);
        }
      }
    );

    // eslint-disable-next-line unicorn/prefer-spread
    const leftoverAccounts = concat(...Object.values(platformAccounts));
    const leftoverAccountIds = map(leftoverAccounts, "_id");

    // If they unlink everything then original post is not required.
    if (leftoverAccounts.length === 0) {
      originalPost = null;
    } else {
      originalPost["accounts"] = leftoverAccounts;
      originalPost["accountIds"] = leftoverAccountIds;

      // Apply platform-specific video type logic for mixed platform leftover accounts
      // When there are multiple platforms in leftover accounts, we need to be careful
      // Only apply fb-reel if ALL accounts are Facebook
      const allPlatformTypes = leftoverAccounts.map(
        (acc) => acc.platformType || "IG"
      );
      const uniquePlatformTypes = [...new Set(allPlatformTypes)];

      if (uniquePlatformTypes.length === 1) {
        // All accounts are the same platform type
        originalPost = applyPlatformSpecificVideoType(
          originalPost,
          uniquePlatformTypes[0]
        );
      } else if (
        originalPost.videoPostType === "reel" &&
        originalPost.isVideo
      ) {
        // Mixed platforms with reel - default to video type for safety
        originalPost.type = "video";
        delete originalPost.isFbReel;
      }
    }

    if (originalPost?.newPost) {
      delete originalPost["_id"];
      delete originalPost["newPost"];
      newPosts.push(originalPost);
      originalPost = null;
    }
  }

  return { originalPost: originalPost as Post, newPosts: newPosts as Post[] };
};

const cleanPost = (post: Post): Post => {
  const cleanedPost = cloneDeep(post) as Post & GoogleMyBusinessPost;

  // Ensure caption and firstcomment are always strings
  // Archived posts from MongoDB may have these as {} (empty objects)
  if (typeof cleanedPost.caption !== "string") {
    cleanedPost.caption = "";
  }
  if (typeof cleanedPost.firstcomment !== "string") {
    cleanedPost.firstcomment = "";
  }

  // Remove fields if a story post
  if (cleanedPost.isStory) {
    cleanedPost["redirectUrl"] = "";
    cleanedPost["firstcomment"] = "";
    cleanedPost["tags"] = [];
    cleanedPost["productTags"] = [];
  }

  if (cleanedPost.gmbTopicType) {
    switch (cleanedPost.gmbTopicType) {
      case "STANDARD": {
        delete cleanedPost[FIELDS.GMB_EVENT];
        delete cleanedPost[FIELDS.GMB_OFFER];
        break;
      }
      case "OFFER": {
        delete cleanedPost["gmbCallToAction"];
        break;
      }
      case "EVENT": {
        break;
      }
      case "ALERT": {
        delete cleanedPost[FIELDS.GMB_EVENT];
        delete cleanedPost[FIELDS.GMB_OFFER];
        break;
      }
    }
  }

  // Remove gmbCallToAction if actionType is null/undefined/"null" to prevent backend validation errors
  if (
    cleanedPost.gmbCallToAction &&
    (!cleanedPost.gmbCallToAction.actionType ||
      cleanedPost.gmbCallToAction.actionType === "null")
  ) {
    delete cleanedPost.gmbCallToAction;
  }

  return cleanedPost;
};

// --------------------- WATCHERS --------------------- //

export default function* postsSagas(): SagaIterator {
  // Use takeLatest for fetch operations to cancel stale requests when accounts are toggled rapidly
  yield takeLatest(INIT_POSTS_PAGE_ROUTINE.TRIGGER, initPostsPage);
  yield takeLatest(FETCH_POSTS_ROUTINE.TRIGGER, fetchPosts);
  yield takeEvery(FETCH_DRAFT_POSTS_ROUTINE.TRIGGER, fetchDrafts);
  yield takeLatest(RELOAD_POSTS_ROUTINE.TRIGGER, reloadPosts);
  yield takeEvery(REMOVE_POSTS_ROUTINE.TRIGGER, removePosts);
  yield takeEvery(REMOVE_POST_ROUTINE.TRIGGER, removePost);
  yield takeEvery(
    SEND_POSTS_TO_QUEUE_ROUTINE.TRIGGER,
    sagaErrorHandlerWrapper({ saga: sendPostsToQueue })
  );
  yield takeEvery(SEND_POST_TO_QUEUE_ROUTINE.TRIGGER, sendPostToQueue);
  yield takeEvery(SEND_POSTS_TO_DRAFT_ROUTINE.TRIGGER, sendPostsToDraft);
  yield takeEvery(SEND_POST_TO_DRAFT_ROUTINE.TRIGGER, sendPostToDraft);
  yield takeEvery(SHUFFLE_QUEUE_ROUTINE.TRIGGER, shuffleQueue);
  yield takeEvery(SORT_POST_ROUTINE.TRIGGER, sortPost);
  yield takeEvery(LOAD_MORE_POSTS_ROUTINE.TRIGGER, loadMorePosts);
  yield takeEvery(EDIT_POST_URL_ROUTINE.TRIGGER, editPostUrl);
  yield takeEvery(TOGGLE_COLLABORATE_WIDGET, toggleCollaborateWidget as any);
  yield takeLatest(CHANGE_POSTS_PAGINATION.TRIGGER, changePostsPagination);
  yield takeEvery(SAVE_POST_FORM_ROUTINE.TRIGGER, savePostForm);
  yield takeEvery(START_POST_FORM_ROUTINE.TRIGGER, startPostForm);
  yield takeEvery(SEND_POST_NOTIFICATION_ROUTINE.TRIGGER, sendPostNotification);
}
